import { useId, useRef, useState, type KeyboardEvent } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { availableSkillsQueryOptions } from "@/lib/data/react-query/skills";
import type { SkillSummary } from "@/lib/data/schemas/skills";

interface SkillMention {
  start: number;
  end: number;
  query: string;
}

export function useChatSkillMentions({
  draft,
  disabled,
  previewSkills,
  onInsert,
}: {
  draft: string;
  disabled?: boolean;
  previewSkills?: SkillSummary[];
  onInsert: (value: string, caret: number) => void;
}) {
  const id = useId();
  const [mention, setMention] = useState<SkillMention>();
  const [activeIndex, setActiveIndex] = useState(0);
  const selectedNames = useRef(new Set<string>());
  const query = mention?.query ?? "";
  const search = useDebouncedValue(query, 150);
  const open = Boolean(mention && !disabled);
  const skills = useInfiniteQuery({
    ...availableSkillsQueryOptions(search),
    enabled: open && previewSkills === undefined && query === search,
    staleTime: 30_000,
  });
  const items = previewSkills
    ? previewSkills.filter((skill) =>
        `${skill.name} ${skill.nativeName} ${skill.description}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      )
    : query === search
      ? (skills.data?.pages.flatMap((page) => page.items) ?? [])
      : [];
  const pending =
    previewSkills === undefined && (query !== search || skills.isPending);
  const selectedIndex = Math.min(activeIndex, Math.max(0, items.length - 1));

  const update = (value: string, caret: number, end = caret) => {
    const match =
      caret === end && value.slice(0, caret).match(/(?:^|\s)@([^\s@]*)$/);
    if (!match || match[1].length > 200) {
      setMention(undefined);
      return;
    }
    const start = caret - match[1].length - 1;
    if (mention?.query !== match[1] || mention.start !== start)
      setActiveIndex(0);
    const suffix = value.slice(caret).match(/^[^\s@]*/)?.[0] ?? "";
    setMention({ start, end: caret + suffix.length, query: match[1] });
  };

  const select = (skill: SkillSummary) => {
    if (!mention || disabled) return;
    const token = `@${skill.nativeName} `;
    selectedNames.current.add(skill.nativeName);
    onInsert(
      draft.slice(0, mention.start) + token + draft.slice(mention.end),
      mention.start + token.length,
    );
    setMention(undefined);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || event.nativeEvent.isComposing) return false;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setMention(undefined);
      return true;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (items.length)
        setActiveIndex(
          (selectedIndex + (event.key === "ArrowDown" ? 1 : items.length - 1)) %
            items.length,
        );
      return true;
    }
    if (
      (event.key === "Enter" && !event.shiftKey) ||
      (event.key === "Tab" && items.length)
    ) {
      event.preventDefault();
      if (items[selectedIndex]) select(items[selectedIndex]);
      return true;
    }
    return false;
  };

  return {
    id,
    open,
    items,
    selectedIndex,
    pending,
    error: previewSkills === undefined && query === search && skills.isError,
    hasMore:
      previewSkills === undefined && query === search && skills.hasNextPage,
    loadingMore: skills.isFetchingNextPage,
    retry: () => void skills.refetch(),
    loadMore: () => void skills.fetchNextPage(),
    update,
    select,
    setActiveIndex,
    close: () => {
      setMention(undefined);
    },
    onKeyDown,
    serialize: (text: string) =>
      text.replace(
        /(^|\s)@([a-zA-Z0-9-]+)(?=$|\s|[.,!?;:])/g,
        (token: string, prefix: string, name: string) =>
          selectedNames.current.has(name) ? `${prefix}$${name}` : token,
      ),
  };
}
